import React from 'react'
import { useState, useEffect, useRef } from 'react'
import { useNavigate, useOutletContext, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import cogoToast from '@successtar/cogo-toast'
import { useConfigStore } from '@renderer/stores/configState'
import { FrameOption, FrameConfig } from '@renderer/types/configState'
import PrintPreview, { PrintPreviewRef } from '@renderer/components/PrintPreview'
import PhotoFrame from '@renderer/components/PhotoFrame'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { backgroundVideoProcessor } from '@renderer/utils/backgroundVideoProcessor'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { usePrintStore } from '@renderer/stores/printStore'
import {
  processAndUploadStrip,
  processAndUploadGIF,
  processAndUploadPhotos,
  stateUploader
} from '@renderer/utils/pipeline/outputOrchestrator'
import { initiatePrint } from '@renderer/utils/pipeline/printService'
import type { ProcessingContext } from '@renderer/types/outputPipeline.types'
import { useTranslation } from 'react-i18next'
import { waitPrintResultMinDisplay } from '@renderer/utils/printResultMinDisplay'
import { navigateAfterOutputsReady } from '@renderer/utils/navigateAfterOutputs'
import { resolveFramedVideoPlaybackAndRemote } from '@renderer/utils/galleryUrlUtils'

interface UserContext {
  user?: {
    email: string
  }
}

const SelectPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const context = useOutletContext<UserContext | null>()
  const { setSessionState, gallery, galleryKey, userPreferences, id } =
    useSessionStateStore()
  const { getFrame, config: globalConfig, frames } = useConfigStore()
  const config = globalConfig?.pages.select
  const { primaryColor, secondaryColor } = globalConfig?.theme || {
    primaryColor: '#3B82F6',
    secondaryColor: '#FFFFFF'
  }
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([])
  const [remainingTime, setRemainingTime] = useState<string>('--')
  const [isUploading, setIsUploading] = useState(false)
  const previewRef = useRef<PrintPreviewRef>(null)
  const [mirroredPhotos, setMirroredPhotos] = useState<Set<string>>(new Set())
  const [isAllMirrored, setIsAllMirrored] = useState(false)
  const [isSelectionLocked, setIsSelectionLocked] = useState(false)
  const hasProcessedRef = useRef(false)
  const { downloadLocal } = useSettingsStore()
  const [videoProcessingId, setVideoProcessingId] = useState<string | null>(null)
  const { isPrintingEnabled, getPrinterForFrame } = usePrintStore()
  const uploadStartedRef = useRef<boolean>(false)
  const printJobSentRef = useRef<boolean>(false)
  const isOnlineRef = useRef<boolean>(true)
  const isMountedRef = useRef(true)

  // Initialize countdown hook
  const selectionCountdown = useCountdown(config?.settings?.selectionTimeout || 0)

  const getFrameConfig = (frame: FrameOption | null): FrameConfig => {
    if (!frame)
      return {
        id: '',
        baseImage: '',
        drawParams: {
          canvasWidth: 1200,
          canvasHeight: 1800,
          backgroundColor: '#FFFFFF',
          images: []
        }
      }
    return {
      id: frame._id,
      baseImage: frame.thumbnail,
      drawParams: frame.drawParams
    }
  }

  const maybeFrame = getFrame(userPreferences.frameId || frames[0]._id)
  const selectedFrame = maybeFrame || null
  const frameConfig = getFrameConfig(selectedFrame)

  // Get unique photo types from frame configuration
  const uniquePhotoTypes = frameConfig.drawParams.images
    .filter((img) => img.type.startsWith('photo-'))
    .map((img) => img.type)
    .filter((value, index, self) => self.indexOf(value) === index)

  const maxSelectablePhotos = uniquePhotoTypes?.length || 0

  const isPotrait =
    (globalConfig?.screenSize.height || 1080) > (globalConfig?.screenSize.width || 1920)

  // FIX 1: Make handleCountdownComplete use a callback to ensure selectedPhotos is current
  const handleCountdownComplete = (): void => {
    setIsSelectionLocked(true)
    setSelectedPhotos((prevSelected: string[]) => {
      let finalSelection = prevSelected

      if (prevSelected.length < maxSelectablePhotos) {
        const remainingNeeded = maxSelectablePhotos - prevSelected.length
        const unselectedPhotos = gallery.capturedItems
          .filter((item) => !prevSelected.includes(item.photoUrl))
          .slice(0, remainingNeeded)
        const autoSelected = unselectedPhotos.map((item) => item.photoUrl)
        finalSelection = [...prevSelected, ...autoSelected]
      }

      // Use setTimeout to ensure state has been updated before calling handleContinue
      setTimeout(() => {
        handleContinueWithPhotos(finalSelection)
      }, 1500)

      return finalSelection
    })
  }

  const handleContinueWithPhotos = async (photosToProcess: string[]): Promise<void> => {
    selectionCountdown.stop()
    setIsUploading(true)
    setIsSelectionLocked(true)
    const processingStartedAt = Date.now()
    let didAttemptPrint = false

    // Log processing start
    setSessionState({
      timeline: {
        activityLogs: [
          {
            page: 'select',
            type: 'processing',
            action: 'start',
            details: {
              selectedCount: photosToProcess.length,
              selectedPhotos: photosToProcess,
              useGIF: userPreferences.useGIF
            },
            timestamp: new Date()
          }
        ]
      }
      // Stats tracked by Gallery API
    })

    try {
      // Generate and upload photo strip
      const stripBlob = await previewRef.current?.exportImage()
      if (!stripBlob)
        throw new Error(t('select.stripExportFailed', 'Failed to create strip preview'))

      if (isPrintingEnabled && !printJobSentRef.current) {
        didAttemptPrint = true
        printJobSentRef.current = true
        const { printerName, settings } = getPrinterForFrame(userPreferences.frameId || '')
        try {
          await initiatePrint(
            stripBlob,
            printerName,
            userPreferences.copies,
            settings,
            isOnlineRef.current
          )
        } catch {
          // Logged in printService
        }
      }

      const stripCtx: ProcessingContext = {
        context,
        projectId,
        sessionId: id || '',
        galleryId: galleryKey || gallery.id,
        downloadLocal,
        isOnline: isOnlineRef.current
      }
      const stripResult = await processAndUploadStrip(stripBlob, stripCtx)
      const localUrl = stripResult?.localUrl ?? URL.createObjectURL(stripBlob)
      setSessionState({
        gallery: {
          capturedItems: [
            ...gallery.capturedItems,
            {
              id: `dreambooth-strip-${id}`,
              stripUrl: localUrl,
              photoUrl: '',
              timestamp: new Date()
            }
          ]
        }
      })

      // FIX 3: Generate and upload framed video in background with proper mirroring
      if (
        userPreferences.useRecording &&
        ['recording', 'strip_live', 'strip_story'].includes(userPreferences.recordingType || '')
      ) {
        const resources = photosToProcess.map((photoUrl) => {
          const item = gallery.capturedItems.find((item) => item.photoUrl === photoUrl)
          if (!item) throw new Error(`Could not find captured item for photo ${photoUrl}`)

          return {
            photoUrl: item.photoUrl,
            videoUrl: item.recordingUrl || ''
          }
        })

        // Get photo states from PrintPreview to ensure mirroring consistency
        const photoStates = previewRef.current?.getPhotoStates() || {}

        // Generate both story and live mode videos
        const taskIds = {
          sequential: `video-story-${id}-${Date.now()}`,
          live: `video-live-${id}-${Date.now()}`
        }
        setVideoProcessingId(
          userPreferences.recordingType === 'strip_story' ? taskIds.sequential : taskIds.live
        ) // Track sequential video initially

        // Start sequential video processing
        backgroundVideoProcessor.processVideo({
          id: taskIds.sequential,
          type: userPreferences.recordingType === 'strip_story' ? 'story' : 'live',
          resources,
          frameConfig,
          sessionId: id || '',
          userId: context?.user?.email || '',
          projectId: projectId || '',
          galleryId: gallery.id,
          speed: globalConfig?.pages.capture.settings.recordingSpeed || 1,
          fps: 30,
          photoStates: photoStates, // Use adjusted photo states
          countdown: globalConfig?.pages.capture.settings.captureCountdown || 1000,
          onComplete: (uploadedUrl, localUrl) => {
            const { playbackUrl, videoRemoteUrl } = resolveFramedVideoPlaybackAndRemote(
              uploadedUrl,
              localUrl
            )
            const logUrl = videoRemoteUrl || playbackUrl
            if (!logUrl) return
            setSessionState({
              gallery: {},
              timeline: {
                activityLogs: [
                  {
                    page: 'select',
                    type: 'output',
                    action: 'upload',
                    details: {
                      type: 'video',
                      mode: userPreferences.recordingType === 'strip_story' ? 'story' : 'live',
                      url: logUrl
                    },
                    timestamp: new Date()
                  }
                ]
              }
            })
          },
          onError: (error) => {
            console.error('Error processing sequential video:', error)
            cogoToast.error(t('select.errorVideo', 'Error processing sequential video'))
            setVideoProcessingId(null)
          }
        })
      }

      stateUploader.uploadSessionState()
      const minDisplaySec = useSettingsStore.getState().printResultMinDisplaySeconds
      if (didAttemptPrint && minDisplaySec > 0) {
        await waitPrintResultMinDisplay(processingStartedAt, minDisplaySec, {
          isStillMounted: () => isMountedRef.current
        })
      }
      navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
    } catch (error) {
      console.error('Error in continue process:', error)
      cogoToast.error(
        t(
          'select.errorProcessing',
          'There was an error processing your photos. Please report this issue.'
        )
      )

      // Log processing failure
      setSessionState({
        timeline: {
          activityLogs: [
            {
              page: 'select',
              type: 'processing',
              action: 'failure',
              details: {
                error: error instanceof Error ? error.message : 'Unknown error',
                selectedCount: photosToProcess.length
              },
              timestamp: new Date()
            }
          ]
        }
      })
      navigateAfterOutputsReady(navigate, projectId!, useConfigStore.getState().config)
    } finally {
      setIsUploading(false)
      setIsSelectionLocked(false)
    }
  }

  // Skip selection if disabled
  useEffect(() => {
    const autoSelectAndProcess = async (): Promise<void> => {
      if (!config?.settings.enabled && !hasProcessedRef.current) {
        hasProcessedRef.current = true

        try {
          // Calculate how many photos we need based on frame configuration
          const photoSlots = frameConfig.drawParams.images.filter((img) =>
            img.type.startsWith('photo-')
          ).length

          // Auto select first N photos
          const autoSelected = gallery.capturedItems.slice(0, photoSlots)
          const selectedUrls = autoSelected.map((item) => item.photoUrl)
          setSelectedPhotos(selectedUrls)

          // Give more time for PrintPreview to be ready
          await new Promise((resolve) => setTimeout(resolve, 2000))

          // Call handleContinueWithPhotos with the selected photos
          await handleContinueWithPhotos(selectedUrls)
        } catch (error) {
          console.error('Error in auto processing:', error)
          cogoToast.error(
            t(
              'select.errorProcessing',
              'There was an error processing your photos. Please try again.'
            )
          )
          hasProcessedRef.current = false
        }
      }
    }

    autoSelectAndProcess()
  }, [config?.settings.enabled, frameConfig, gallery.capturedItems])

  // Initialize countdown and handle initial media uploads
  useEffect((): void | (() => void) => {
    isMountedRef.current = true
    // Upload session state on mount
    stateUploader.uploadSessionState()

    const selectionTimeout = config?.settings?.selectionTimeout
    if (selectionTimeout && selectionTimeout > 0) {
      // Set up countdown callbacks
      selectionCountdown.setOnTick(() => {
        setRemainingTime(selectionCountdown.getFormattedRemainingSeconds())
      })

      selectionCountdown.setOnComplete(() => {
        setSessionState({
          timeline: {
            activityLogs: [
              {
                page: 'select',
                type: 'selection',
                action: 'timeout',
                details: {
                  selectedCount: selectedPhotos.length,
                  timeoutDuration: selectionTimeout
                },
                timestamp: new Date()
              }
            ]
          }
        })
        handleCountdownComplete()
      })

      selectionCountdown.start()
    }

    // Initialize media uploads
    const initializeMedia = async (): Promise<void> => {
      // Prevent concurrent upload attempts
      if (uploadStartedRef.current) {
        return
      }
      uploadStartedRef.current = true

      // Create and upload both MP4 and GIF formats if enabled - fire in background
      if (userPreferences.useGIF) {
        (async (): Promise<void> => {
          const photoUrls = gallery.capturedItems
            .filter((item) => item.photoUrl)
            .map((item) => item.photoUrl!) as string[]
          const gifSpeed = globalConfig?.pages.capture?.settings?.gifSpeed || 1000
          const customGifFrame = globalConfig?.pages.capture?.settings?.customGifFrame
          const initCtx: ProcessingContext = {
            context,
            projectId,
            sessionId: id || '',
            galleryId: galleryKey || gallery.id,
            downloadLocal,
            isOnline: isOnlineRef.current
          }
          try {
            const { gifLocalUrl, mp4LocalUrl } = await processAndUploadGIF(
              photoUrls,
              gifSpeed,
              initCtx,
              customGifFrame
            )
            const newItems: Array<{
              id: string
              animationUrl: string
              animationType: 'mp4' | 'gif'
              gifUrl: string
              photoUrl: string
              timestamp: Date
            }> = []
            if (mp4LocalUrl) {
              newItems.push({
                id: `dreambooth-animation-mp4-${id}`,
                animationUrl: mp4LocalUrl,
                animationType: 'mp4',
                gifUrl: '',
                photoUrl: '',
                timestamp: new Date()
              })
            }
            if (gifLocalUrl) {
              newItems.push({
                id: `dreambooth-animation-gif-${id}`,
                animationUrl: gifLocalUrl,
                animationType: 'gif',
                gifUrl: gifLocalUrl,
                photoUrl: '',
                timestamp: new Date()
              })
            }
            if (newItems.length > 0) {
              setSessionState({
                gallery: {
                  capturedItems: [...gallery.capturedItems, ...newItems]
                }
              })
            } else if (isOnlineRef.current) {
              cogoToast.error('Failed to create both animation formats')
            }
          } catch (err) {
            if (isOnlineRef.current) cogoToast.error('Failed to create animations')
            console.warn('[Select] GIF/MP4 creation/upload failed:', err)
          }
        })()
      }

      const photoCtx: ProcessingContext = {
        context,
        projectId,
        sessionId: id || '',
        galleryId: galleryKey || gallery.id,
        downloadLocal,
        isOnline: isOnlineRef.current
      }
      const photoItems = gallery.capturedItems
        .filter((item) => item.photoUrl && item.photoUrl.trim() !== '')
        .map((item) => ({ photoUrl: item.photoUrl! }))
      try {
        await Promise.race([
          processAndUploadPhotos(photoItems, photoCtx),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Photo uploads timed out')), 15000)
          )
        ])
        const g = useSessionStateStore.getState().gallery
        setSessionState({
          gallery: {
            ...g,
            individualPhotosUploaded: true,
            individualPhotosUploadedAt: Date.now()
          }
        })
      } catch (err) {
        cogoToast.warn('Photo uploads timed out, continuing with partial results')
        console.error('Error initializing media:', err)
        if (isOnlineRef.current) {
          cogoToast.error(t('select.errorUploading', 'Error uploading media'))
        }
      }
    }

    initializeMedia()

    return (): void => {
      isMountedRef.current = false
      selectionCountdown.stop()
    }
  }, [])

  // Check internet connectivity for offline detection
  useEffect(() => {
    if (typeof window.api !== 'undefined' && window.api.getInternetStatus) {
      window.api.getInternetStatus().then((status: boolean) => {
        isOnlineRef.current = status
      })

      const unsubscribe = window.api.onInternetStatusChanged((status: boolean) => {
        isOnlineRef.current = status
      })

      return unsubscribe
    }
    return (): void => {}
  }, [])

  // Add effect to check video processing status
  useEffect(() => {
    if (!videoProcessingId) return

    const checkStatus = (): void => {
      const isProcessing = backgroundVideoProcessor.isTaskProcessing(videoProcessingId)
      if (!isProcessing) {
        setVideoProcessingId(null)
      }
    }

    const interval = setInterval(checkStatus, 1000)
    return (): void => clearInterval(interval)
  }, [videoProcessingId])

  const handlePhotoClick = (photoUrl: string): void => {
    if (isSelectionLocked) return

    setSelectedPhotos((prev) => {
      let newSelection
      if (prev.includes(photoUrl)) {
        newSelection = prev.filter((url) => url !== photoUrl)
      } else if (prev.length < maxSelectablePhotos) {
        newSelection = [...prev, photoUrl]
      } else {
        newSelection = prev
      }
      return newSelection
    })
  }

  // Modified handleContinue to use the new function
  const handleContinue = async (): Promise<void> => {
    await handleContinueWithPhotos(selectedPhotos)
  }

  // Separate countdown component to prevent preview re-renders
  const CountdownDisplay = React.memo(() => (
    <span className="text-center text-carbon text-3xl">{remainingTime}</span>
  ))
  CountdownDisplay.displayName = 'CountdownDisplay'

  // If selection is disabled, show loading state with PrintPreview underneath
  if (!config?.settings.enabled) {
    return (
      <div className="w-full h-full flex bg-whiteout">
        {/* Hidden PrintPreview for auto-processing */}
        <div className="hidden">
          <PrintPreview
            ref={previewRef}
            frameConfig={frameConfig}
            selectedPhotos={selectedPhotos.map((photo) =>
              mirroredPhotos.has(photo) ? `mirror:${photo}` : photo
            )}
            isInteractive={!isSelectionLocked}
            sessionId={id || ''}
          />
        </div>

        {/* Loading Overlay */}
        <div className="absolute inset-0 flex items-center justify-center bg-whiteout z-50">
          <div className="flex flex-col items-center gap-8 p-8 bg-white rounded-2xl shadow-2xl">
            <h1 className="text-4xl text-carbon font-bold">
              {t('select.processingPhotos', 'Processing Photos')}
            </h1>
            <div
              className="w-16 h-16 border-4 border-t-transparent rounded-full animate-spin"
              style={{
                borderColor: primaryColor,
                borderTopColor: 'transparent'
              }}
            />
            <p className="text-lg text-carbon/60">
              {t('select.pleaseWait', 'Please wait while we process your photos...')}
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Call all hooks at the top level
  const cachedSelectBg = useCachedBackgroundImage('/assets/photophoria/photophoria-select.webp')
  const cachedSelectMirror = useCachedImage('/assets/photophoria/photophoria-select-mirror.webp')
  const cachedFrameButton = useCachedImage('/assets/photophoria/photophoria-frame-button.webp')

  return (
    <div
      className="w-full h-full flex bg-cover bg-center"
      data-page="select"
      style={{
        backgroundImage: `url(${cachedSelectBg})`
      }}
    >
      {/* Left Content - Photo Selection */}
      <div className="flex-1 flex flex-col w-full max-w-[70%] pb-8 mr-24 mb-16">
        <div className="px-12 mt-20 mb-4 flex items-center justify-center">
          <button
            type="button"
            onClick={() => {
              if (isSelectionLocked) return
              // Mirror all photos
              const newMirroredPhotos = new Set(mirroredPhotos)
              const willMirrorAll = gallery.capturedItems.some(
                (item) => !mirroredPhotos.has(item.photoUrl)
              )

              gallery.capturedItems.forEach((item) => {
                if (willMirrorAll) {
                  newMirroredPhotos.add(item.photoUrl)
                } else {
                  newMirroredPhotos.delete(item.photoUrl)
                }
              })

              setIsAllMirrored(willMirrorAll)
              setMirroredPhotos(newMirroredPhotos)
            }}
            disabled={isSelectionLocked}
            title={
              isSelectionLocked
                ? t('select.selectionLocked', 'Selection locked')
                : t('select.mirror', 'Mirror Photos')
            }
            className={`flex items-center gap-2 rounded-lg transition-all duration-300 ${isSelectionLocked ? 'opacity-50 cursor-not-allowed' : ''} ${isAllMirrored ? 'scale-110' : ''}`}
          >
            <img
              src={cachedSelectMirror}
              alt={t('select.mirror', 'Mirror Photos')}
              className={`w-[200px] h-auto`}
            />
          </button>
        </div>
        <div
          className={`flex-1 overflow-y-auto pl-36 pr-4 mt-12 ${isSelectionLocked ? 'pointer-events-none' : ''}`}
        >
          {isSelectionLocked && (
            <div className="absolute inset-0 bg-carbon/5 backdrop-blur-[1px] z-10 flex items-center justify-center">
              <div className="bg-white/90 px-6 py-3 rounded-lg shadow-lg">
                <span className="text-xl text-carbon">
                  {isUploading
                    ? t('select.processing', 'Processing photos...')
                    : t('select.selectionLocked', 'Selection locked')}
                </span>
              </div>
            </div>
          )}
          <div className={`grid ${isPotrait ? 'grid-cols-1' : 'grid-cols-3'} gap-6`} role="listbox">
            {gallery.capturedItems.map((item, index) => {
              if (!item.photoUrl) return null
              const isSelected = selectedPhotos.includes(item.photoUrl)
              const isMirrored = mirroredPhotos.has(item.photoUrl)
              return (
                <div key={index} className="relative group">
                  <button
                    type="button"
                    onClick={() => handlePhotoClick(item.photoUrl)}
                    disabled={isSelectionLocked}
                    role="option"
                    className={`
                      group relative overflow-hidden rounded-xl transition-all duration-300 w-full
                      ${isSelected ? 'ring-4 scale-95' : 'hover:scale-[0.98]'}
                      ${isSelectionLocked ? 'opacity-75 cursor-not-allowed' : ''}
                    `}
                    style={{
                      boxShadow: isSelected ? `0 0 0 4px ${primaryColor}` : ''
                    }}
                  >
                    <PhotoFrame
                      src={item.photoUrl || ''}
                      alt={t('capture.lastCaptured', 'Capture {{number}}', { number: index + 1 })}
                      displayRatio={userPreferences.framePhotoRatio || 1}
                      isMirrored={isMirrored}
                      className={isSelected ? 'grayscale' : ''}
                    />
                    <div
                      className={`
                        absolute inset-0 flex items-center justify-center
                        bg-black/50 transition-opacity duration-300
                        ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
                      `}
                    >
                      <div className="text-center text-white">
                        <div className="text-4xl mb-2">{isSelected ? '✓' : '+'}</div>
                        <p className="text-lg font-medium">
                          {isSelected
                            ? t('select.selected', 'Selected')
                            : t('select.clickToSelect', 'Click to Select')}
                        </p>
                      </div>
                    </div>
                    {isSelected && (
                      <div
                        className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center shadow-lg"
                        style={{
                          backgroundColor: primaryColor
                        }}
                      >
                        <span
                          style={{
                            color: secondaryColor
                          }}
                          className="text-lg font-bold"
                        >
                          {selectedPhotos.indexOf(item.photoUrl || '') + 1}
                        </span>
                      </div>
                    )}
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Right Panel - Print Preview */}
      <div
        className={`flex flex-col items-center justify-center mt-48 w-full ${isPotrait ? 'p-12 max-w-[40%]' : 'p-8 max-w-[32%]'}`}
      >
        <div className="w-[350px] px-4 mt-8 flex flex-col items-center gap-2">
          <PrintPreview
            ref={previewRef}
            frameConfig={frameConfig}
            selectedPhotos={selectedPhotos.map((photo) =>
              mirroredPhotos.has(photo) ? `mirror:${photo}` : photo
            )}
            className="w-full shadow-xl drop-shadow-xl"
            sessionId={id || ''}
          />
          <CountdownDisplay />
        </div>

        <div className="flex flex-col items-center gap-4 mt-16">
          {/* Next Button */}
          <button
            onClick={handleContinue}
            disabled={selectedPhotos.length !== maxSelectablePhotos || isUploading}
            className={`transition-transform transform hover:scale-105 ${selectedPhotos.length !== maxSelectablePhotos || isUploading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <img src={cachedFrameButton} alt={t('select.next', 'Next')} className="w-60 h-auto" />
          </button>
        </div>
      </div>
    </div>
  )
}

export default SelectPage
